import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { Member } from '../../types';
import api from '../../config/api';
import { format } from 'date-fns';

export const MemberForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    member_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    join_date: format(new Date(), 'yyyy-MM-dd'),
    membership_status: 'active',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchMember();
    }
  }, [id, isEdit]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/members/${id}`);
      const member = response.data;
      setFormData({
        member_code: member.member_code || '',
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
        phone: member.phone || '',
        company: member.company || '',
        position: member.position || '',
        join_date: member.join_date ? format(new Date(member.join_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        membership_status: member.membership_status || 'active',
        notes: member.notes || '',
      });
    } catch (error: any) {
      console.error('Error fetching member:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to load member data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await api.put(`/members/${id}`, formData);
      } else {
        await api.post('/members', formData);
      }
      navigate('/members');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to save member';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Member' : 'Add New Member'}
      </Typography>

      <Paper elevation={2} sx={{ p: 3 }}>
        {loading && isEdit ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 30%" } }}>
              <TextField
                fullWidth
                label="Member Code"
                name="member_code"
                value={formData.member_code}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 30%" } }}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 30%" } }}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                label="Join Date"
                name="join_date"
                type="date"
                value={formData.join_date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
              <TextField
                fullWidth
                select
                label="Status"
                name="membership_status"
                value={formData.membership_status}
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ flex: "1 1 100%" }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Box>
          </Box>

          <Box mt={3} display="flex" gap={2}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate('/members')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>
        )}
      </Paper>
    </Box>
  );
};
